import React from 'react';
import { SortableSection } from './SortableSection';
import { Section } from '../../stores/builderStore';
import { Plus } from 'lucide-react';
import { useBuilderStore } from '../../stores/builderStore';

interface CanvasProps {
  sections: Section[];
  selectedId: string | null;
  onSelectSection: (id: string | null) => void;
  device: 'desktop' | 'tablet' | 'mobile';
}

export const Canvas: React.FC<CanvasProps> = ({
  sections,
  selectedId,
  onSelectSection,
  device
}) => {
  const addSection = useBuilderStore(state => state.addSection);

  // Device dimensions
  const deviceDimensions = {
    desktop: { width: '100%', height: '100%' },
    tablet: { width: '768px', height: '1024px' },
    mobile: { width: '375px', height: '812px' }
  };

  const deviceScale = {
    desktop: 1,
    tablet: 0.75,
    mobile: 0.75
  };

  return (
    <div className="h-full flex items-center justify-center p-8 overflow-y-auto bg-gray-100">
      {/* Device Container */}
      <div
        className="relative"
        style={{
          transform: `scale(${deviceScale[device]})`,
          transformOrigin: 'center center'
        }}
      >
        {/* Device Frame */}
        <div
          className={`relative bg-black ${
            device === 'mobile' ? 'rounded-[3rem] p-3' :
            device === 'tablet' ? 'rounded-2xl p-4' : ''
          }`}
          style={{
            width: deviceDimensions[device].width,
            height: device === 'desktop' ? 'auto' : deviceDimensions[device].height,
            maxHeight: device === 'desktop' ? 'calc(100vh - 200px)' : deviceDimensions[device].height,
            boxShadow: device !== 'desktop' ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' : 'none'
          }}
        >
          {/* Mobile Status Bar */}
          {device === 'mobile' && (
            <div className="absolute top-0 left-0 right-0 h-12 bg-black rounded-t-[2.5rem] z-10">
              <div className="flex justify-between items-center px-6 pt-2 text-white text-xs">
                <span>9:41</span>
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M2 17h20v2H2zm1.15-4.05L4 11.47l.85 1.48 1.3-.75-.85-1.48H7v-1.5H2v1.5h2.15L3 10.72zm17.7 0L19.88 11l.62-1.08H18v-1.5h5v1.5h-2.15l1.15 2z"/>
                  </svg>
                  <svg className="w-4 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.01 21.49L23.64 7c-.45-.34-4.93-4-11.64-4C5.28 3 .81 6.66.36 7l11.63 14.49.01.01.01-.01z"/>
                  </svg>
                  <svg className="w-5 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="2" y="7" width="19" height="10" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
                    <rect x="3" y="9" width="14" height="6" fill="currentColor"/>
                    <rect x="21" y="10" width="2" height="4" rx="0.5" fill="currentColor"/>
                  </svg>
                </div>
              </div>
              {/* Notch */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-40 h-7 bg-black rounded-b-3xl" />
            </div>
          )}

          {/* Screen Content */}
          <div
            className={`bg-white relative ${
              device === 'mobile' ? 'rounded-[2.5rem] h-full' :
              device === 'tablet' ? 'rounded-xl h-full' :
              'shadow-xl rounded-lg'
            }`}
            style={{
              height: device === 'desktop' ? 'auto' : '100%',
              maxHeight: device === 'desktop' ? 'calc(100vh - 200px)' : '100%'
            }}
          >
            {/* Scrollable Content Area */}
            <div
              className={`h-full overflow-y-auto overflow-x-hidden scrollbar-thin ${
                device === 'mobile' ? 'pt-12' : ''
              }`}
              style={{
                height: device === 'desktop' ? 'auto' : '100%',
                maxHeight: device === 'desktop' ? 'calc(100vh - 200px)' : '100%',
                width: '100%',
                maxWidth: '100%'
              }}
            >
              {/* Empty State */}
              {sections.length === 0 && (
                <div className="flex items-center justify-center min-h-[400px] p-8">
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                      <Plus className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-medium text-gray-700 mb-2">
                      Start Building Your Homepage
                    </h3>
                    <p className="text-gray-500 mb-6">
                      Add your first section from the component library
                    </p>
                    <button
                      onClick={() => addSection('hero')}
                      className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      Add First Section
                    </button>
                  </div>
                </div>
              )}

              {/* Sections */}
              <div className="relative">
                {sections.map((section, index) => (
                  <SortableSection
                    key={section.id}
                    section={section}
                    index={index}
                    isSelected={selectedId === section.id}
                    onSelect={() => onSelectSection(section.id)}
                    device={device}
                  />
                ))}
              </div>

              {/* Add Section Button */}
              {sections.length > 0 && (
                <div className="p-4 border-t border-dashed border-gray-300">
                  <button
                    onClick={() => addSection('cta')}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-500 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Section
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Home Indicator */}
          {device === 'mobile' && (
            <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-white rounded-full" />
          )}
        </div>
      </div>
    </div>
  );
};